import type { MenuId } from './Sidebar';

/**
 * メニューごとの線画アイコン。
 *
 * 絵文字は OS ごとに色も形も変わり、配色が崩れるため使わない。線だけで描いて文字色に
 * 合わせる（印だけが浮かないようにする）。自動化は 1 つ 1 つ違う絵にせず、まとめて
 * 稲妻にする（「自動で動くもの」という括りが伝わればよい）。
 * @graph-connects none
 */
const ICON_PATH: Record<string, string> = {
  // チェックの付いた一覧
  tasks:
    'M3 5.2 4.2 6.4 6.6 4M3 10.2 4.2 11.4 6.6 9M3 15.2 4.2 16.4 6.6 14M9 5.2h8M9 10.2h8M9 15.2h8',
  // 棒グラフ
  analysis: 'M3 17h14M6.5 17V9.5M10 17V4M13.5 17v-5.5',
  // 折り返しのある書類（型）
  'fmt-monthly': 'M5 2.8h6.5L15 6.3v10.9H5zM11.5 2.8v3.5H15M7.6 9.5h4.8M7.6 12.5h4.8',
  // 鎖の輪（外部へのリンク）
  'related-links':
    'M8.4 11.6a3 3 0 0 0 4.2 0l2.4-2.4a3 3 0 0 0-4.2-4.2l-1 1M11.6 8.4a3 3 0 0 0-4.2 0L5 10.8a3 3 0 0 0 4.2 4.2l1-1',
  // 人が二人
  'members-settings':
    'M7.4 9.2a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8ZM3.2 16c0-2.4 1.9-3.9 4.2-3.9s4.2 1.5 4.2 3.9M13.6 8.6a2 2 0 1 0 0-4M14 12.4c2 0 3.4 1.3 3.4 3.4',
  // 値札
  'kinds-settings': 'M3.2 3.2h6L17 11l-5.8 5.8L3.2 9zM6.2 6.2h.01',
  // 稲妻（自動化）
  automation: 'M11.6 2.5 4.6 11h4.2l-.9 6.5 7-8.5h-4.2z',
  // 円のゲージ（進み具合）
  'monthly-progress': 'M10 3a7 7 0 1 0 7 7M10 6.6v3.6l2.6 1.6',
  // 決算の FMT・進捗は、月次と同じ絵にする（同じ種類の画面だと分かるように）
  'fmt-closing': 'M5 2.8h6.5L15 6.3v10.9H5zM11.5 2.8v3.5H15M7.6 9.5h4.8M7.6 12.5h4.8',
  'closing-progress': 'M10 3a7 7 0 1 0 7 7M10 6.6v3.6l2.6 1.6',
  // 家（チームを選び直すところへ戻る）
  home: 'M3 8.6 10 3l7 5.6M4.8 7.9V16.4h10.4V7.9',
};

/**
 * MenuIcon の props。
 */
export interface MenuIconProps {
  /** どのメニューのアイコンか。`home` はメニューではなくホームへ戻る印 */
  id: MenuId | 'home';
  /** 追加のクラス。大きさや色はここで調整する */
  className?: string;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business メニューごとの線画アイコンを描く。自動化（`auto-` で始まるもの）は共通の稲妻にする
 * @graph-connects none
 */
export function MenuIcon({ id, className = '' }: MenuIconProps) {
  const path = ICON_PATH[id.startsWith('auto-') ? 'automation' : id];
  if (path === undefined) {
    return null;
  }
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      strokeLinecap="round"
      className={`inline-block shrink-0 align-[-0.15em] ${className}`}
    >
      <path d={path} />
    </svg>
  );
}
